import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { getData, DashboardConfig } from '../services/api';

const OFFLINE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

type SensorStatus = {
  osm_name:    string;
  description: string | null;
  lastSeen:    Date | null;
  online:      boolean;
};

async function getLastSeen(osmName: string): Promise<Date | null> {
  const now  = new Date();
  const from = new Date(now.getTime() - 24 * 60 * 60 * 1000); // look back 24h

  for (const meas of ['SND', 'LGHT']) {
    try {
      const rows = await getData(
        'pg_sf_ds',
        'bucket',
        from.toISOString(),
        now.toISOString(),
        meas,
        osmName,
      );
      if (rows?.length) {
        const latest = rows[rows.length - 1];
        const ts = latest.bucket ?? latest.time ?? latest.timestamp;
        if (ts) return new Date(ts);
      }
    } catch {
      // try next measurement
    }
  }
  return null;
}

function formatLastSeen(date: Date | null): string {
  if (!date) return 'No data';
  const diffMs  = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1)  return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}h ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

interface Props {
  configs: DashboardConfig[];
}

export default function SensorStatusGrid({ configs }: Props) {
  const [statuses, setStatuses] = useState<SensorStatus[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!configs.length) return;

    async function load() {
      const results = await Promise.all(
        configs.map(async (c): Promise<SensorStatus> => {
          const lastSeen = await getLastSeen(c.osm_name);
          const online   = lastSeen !== null &&
            (Date.now() - lastSeen.getTime()) < OFFLINE_THRESHOLD_MS;
          return {
            osm_name:    c.osm_name,
            description: c.description,
            lastSeen,
            online,
          };
        })
      );
      setStatuses(results);
      setLoading(false);
    }

    load();
  }, [configs]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <CircularProgress size={24} sx={{ color: '#1D9E75' }} />
    </Box>
  );

  const online  = statuses.filter(s => s.online).length;
  const offline = statuses.filter(s => !s.online).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* Summary row */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {online} online
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#993C1D', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {offline} offline
          </Typography>
        </Box>
      </Box>

      {/* Location cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 1,
      }}>
        {statuses.map(sensor => (
          <Box
            key={sensor.osm_name}
            sx={{
              display:        'flex',
              flexDirection:  'column',
              gap:            0.5,
              p:              '10px 12px',
              borderRadius:   2,
              background:     'var(--color-bg-tertiary, rgba(255,255,255,0.04))',
              borderLeft:     `3px solid ${sensor.online ? '#1D9E75' : '#993C1D'}`,
              transition:     'opacity 0.2s',
              opacity:        sensor.online ? 1 : 0.7,
            }}
          >
            {/* Location name */}
            <Typography sx={{
              fontFamily: "'Syne', sans-serif",
              fontSize:   12,
              fontWeight: 600,
              color:      'var(--color-text-primary)',
              lineHeight: 1.3,
              overflow:   'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {sensor.description?.trim() || sensor.osm_name}
            </Typography>

            {/* Status + last seen */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width:        6,
                  height:       6,
                  borderRadius: '50%',
                  flexShrink:   0,
                  background:   sensor.online ? '#1D9E75' : '#993C1D',
                  boxShadow:    sensor.online
                    ? '0 0 6px rgba(29,158,117,0.6)'
                    : '0 0 6px rgba(153,60,29,0.4)',
                }} />
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize:   10,
                  color:      sensor.online ? '#1D9E75' : '#993C1D',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                }}>
                  {sensor.online ? 'Online' : 'Offline'}
                </Typography>
              </Box>

              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize:   10,
                color:      'var(--color-text-secondary)',
              }}>
                {formatLastSeen(sensor.lastSeen)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
